import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useCreatePersonMutation,
  useGetPersonByIdQuery,
  useUpdatePersonMutation,
} from "@/services/api";
import { createTempPerson, transformSDateToString, transformStringToSDate } from "@/shared/utils";
import { appRouterPaths } from "@/services/navigation";
import type { MaybeType } from "@/shared/types";
import type { PersonInfoCardFormValues } from "../types";
import { personInfoSchema } from "../schema";

export const usePersonInfoCard = (selectedPersonId: MaybeType<string>) => {
  const navigate = useNavigate();

  const { data: person } = useGetPersonByIdQuery(
    { id: selectedPersonId ?? "" },
    { skip: !selectedPersonId || selectedPersonId === "TEMP_ID" },
  );

  const [updatePerson] = useUpdatePersonMutation();
  const [createPerson] = useCreatePersonMutation();

  const form = useForm<PersonInfoCardFormValues>({
    resolver: yupResolver(personInfoSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    shouldFocusError: false,
    shouldUnregister: false,
  });

  const onSubmit = async (data: PersonInfoCardFormValues) => {
    if (!selectedPersonId) return;

    const isValid = await form.trigger();

    if (!isValid) return;

    if (selectedPersonId === "TEMP_ID") {
      const newPerson = await createPerson({
        ...createTempPerson(),
        firstName: data.firstName ?? null,
        middleName: data.middleName ?? null,
        lastName: data.lastName ?? null,
        gender: data.gender,
        birthday: data.birthday ?? null,
        additionalInformation: data.additionalInformation ?? null,
        group: data.group ?? null,
        title: data.title ?? null,
      }).unwrap();

      if (newPerson) {
        navigate(
          `${appRouterPaths.contacts.genInfo.replace(":personId", newPerson.id)}isEditMode=true`,
        );
        form.reset({
          firstName: newPerson.firstName,
          middleName: newPerson.middleName,
          lastName: newPerson.lastName,
          gender: newPerson.gender,
          birthday: newPerson.birthday,
          additionalInformation: newPerson.additionalInformation,
          group: newPerson.group ?? "",
          title: newPerson.title ?? "",
        });
      }

      return;
    }

    if (!person) return;

    await updatePerson({
      ...person,
      id: selectedPersonId,
      firstName: data.firstName ?? null,
      middleName: data.middleName ?? null,
      lastName: data.lastName ?? null,
      gender: data.gender,
      birthdaySDate: transformStringToSDate(data.birthday ?? ""),
      additionalInformation: data.additionalInformation ?? null,
      group: data.group ?? null,
      title: data.title ?? null,
    });
  };

  useEffect(() => {
    if (selectedPersonId !== "TEMP_ID") {
      form.reset({
        firstName: person?.firstName,
        middleName: person?.middleName,
        lastName: person?.lastName,
        gender: person?.gender,
        birthday: transformSDateToString(person?.birthdaySDate),
        additionalInformation: person?.additionalInformation,
        group: person?.group ?? "",
        title: person?.title ?? "",
      });
    } else {
      form.reset({
        firstName: null,
        middleName: null,
        lastName: null,
        gender: "NOT_DEFINED",
        birthday: null,
        additionalInformation: null,
        group: "",
        title: "",
      });
    }
  }, [selectedPersonId, person]);

  return {
    state: {
      currentPerson: selectedPersonId === "TEMP_ID" ? createTempPerson() : person,
      form,
    },
    methods: {
      onSubmit,
    },
  };
};
